/**
 * @copyright 2025 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useCallback } from 'react'

import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'

import { useAuth } from '@/hooks'
import { firstAppPath } from '@/utils/appHome'

import { AuditLogsView } from './AuditLogsView'

/**
 * Audit Logs page. The open record id lives in the route
 * (`/audit-logs/:auditId`) so a specific change is deep-linkable.
 *
 * Gated on the `auditLogs` app grant. The route carries no guard of its own, so the page
 * checks the same permission the nav link does; the API enforces it as well.
 */
export const AuditLogsPage: React.FC = () => {
  const { auditId } = useParams<{ auditId: string }>()
  const navigate = useNavigate()
  const { search } = useLocation()
  const { hasPermission } = useAuth()

  const handleOpenRecord = useCallback(
    (id: string | null, nextSearch?: string) => {
      // The filters live in the query string, so it must be carried across an open/close — losing
      // it would silently reset every filter the moment a row is clicked.
      navigate({ pathname: id ? `/audit-logs/${id}` : '/audit-logs', search: nextSearch ?? search })
    },
    [navigate, search]
  )

  if (!hasPermission('auditLogs')) {
    return <Navigate to={firstAppPath(hasPermission)} replace />
  }

  return <AuditLogsView openRecordId={auditId} onOpenRecord={handleOpenRecord} />
}

export default AuditLogsPage
